import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "admin" | "advertiser" | "agency";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  networkError: boolean;
  roles: AppRole[];
  hasRole: (role: AppRole) => boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  networkError: false,
  roles: [],
  hasRole: () => false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// Hard timeout to avoid infinite loading screens when Supabase is unreachable
// (e.g. RU ISP blocking *.supabase.co domains).
const SESSION_INIT_TIMEOUT_MS = 6000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const [roles, setRoles] = useState<AppRole[]>([]);

  const fetchRoles = async (userId: string) => {
    try {
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      if (data) {
        setRoles(data.map((r) => r.role as AppRole));
      }
    } catch (_e) {
      // network issue while fetching roles — keep current roles
    }
  };

  useEffect(() => {
    let finished = false;
    const finishLoading = (hadError = false) => {
      if (finished) return;
      finished = true;
      if (hadError) setNetworkError(true);
      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        // ВАЖНО: внутри колбэка onAuthStateChange выполняем ТОЛЬКО синхронные
        // обновления стейта. Любые async-вызовы (Supabase, fetch) откладываем
        // через setTimeout(..., 0), иначе возможен deadlock и вечный спиннер.
        setSession(nextSession);
        setUser(nextSession?.user ?? null);

        if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN" || event === "SIGNED_OUT") {
          setNetworkError(false);
        }

        // Снимаем загрузку сразу — UI не должен ждать ролей.
        finishLoading(false);

        if (nextSession?.user) {
          setTimeout(() => {
            fetchRoles(nextSession.user.id);
          }, 0);
        } else {
          setRoles([]);
        }
      }
    );

    // Race getSession против таймаута, чтобы UI не висел вечно
    const timeoutPromise = new Promise<{ timeout: true }>((resolve) => {
      setTimeout(() => resolve({ timeout: true }), SESSION_INIT_TIMEOUT_MS);
    });

    Promise.race([
      supabase.auth.getSession().then((res) => ({ timeout: false as const, res })),
      timeoutPromise,
    ])
      .then((result) => {
        if ("res" in result) {
          const { data, error } = result.res;
          if (error) {
            const msg = error.message?.toLowerCase() ?? "";
            if (msg.includes("refresh") || msg.includes("token")) {
              setTimeout(() => {
                supabase.auth.signOut().catch(() => {});
              }, 0);
            }
            finishLoading(false);
            return;
          }
          setSession(data.session);
          setUser(data.session?.user ?? null);
          finishLoading(false);
          if (data.session?.user) {
            setTimeout(() => {
              fetchRoles(data.session!.user.id);
            }, 0);
          }
        } else {
          finishLoading(true);
        }
      })
      .catch(() => finishLoading(true));

    return () => subscription.unsubscribe();
  }, []);

  const hasRole = (role: AppRole) => roles.includes(role);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (_e) {
      /* ignore network errors on sign out */
    }
    setRoles([]);
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, networkError, roles, hasRole, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
